import React from 'react';
import { motion } from 'framer-motion';
import Container from '../../common/container';

export default function AgentsSection({ initialData }) {
  // Image path helper function
  const getImagePath = (path) => {
    if (initialData?.data?.storage_url) {
      const sanitizedPath = path.startsWith('/static/frontend/')
        ? path.replace('/static/frontend/', '')
        : path;
      return `${initialData.data.storage_url}${sanitizedPath}`;
    }
    return path;
  };
  const agentBuilderImage = getImagePath('/static/frontend/images/site/social_commerce_catalogs.png');

  // Enhanced agent data with more compelling descriptions
  const agents = [
    {
      title: "Customer Support Agent",
      description: "Resolve 80% of customer queries instantly, 24/7—even when you're asleep. Customers get immediate answers while your team focuses only on high-value conversations.",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      )
    },
    {
      title: "Order Management Agent",
      description: "Transform order management with automatic confirmations, proactive delivery updates, and hassle-free return processing—all within the WhatsApp chat your customers already use daily.",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      )
    },
    {
      title: "Personalized Shopping Agent",
      description: "Turn browsers into buyers with personalized product recommendations based on preferences and past purchases. Our clients see 40% higher conversion rates compared to traditional e-commerce.",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    },
    {
      title: "Payment Processing Agent",
      description: "Close sales instantly with secure in-chat payments—no redirects, no abandoned carts. Send invoices, process payments, and confirm transactions without customers ever leaving WhatsApp.",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
  ];

  // Business impact metrics
  const metrics = [
    { value: "80%", label: "Queries Automated" },
    { value: "24/7", label: "Availability" },
    { value: "3x", label: "Sales Growth" },
    { value: "5x", label: "Faster Response" }
  ];

  // Steps for how it works
  const steps = [
    { number: "01", title: "Choose agent type", description: "Select from pre-built templates or start from scratch" },
    { number: "02", title: "Customize flows", description: "Use our drag-and-drop builder to design conversation flows" },
    { number: "03", title: "Launch instantly", description: "Deploy to WhatsApp in minutes with a single click" }
  ];

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="py-24 bg-gradient-to-b from-gray-50 to-white"
    >
      <Container>
        {/* Enhanced Header */}
        <motion.div 
          className="mx-auto max-w-3xl text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <span className="inline-flex items-center px-3 py-1 text-sm font-medium text-primary bg-primary/10 rounded-full mb-6">
            24/7 WhatsApp AI Agents
          </span>
          <h2 className="text-4xl font-bold tracking-tight mb-6">
            Your Business Team That Never Sleeps
            <span className="block text-primary mt-2">Launch in Minutes • No Coding Required</span>
          </h2>
          <p className="text-gray-600 text-lg">
            Deploy specialized AI agents directly on WhatsApp—where 2+ billion users already are. 
            Our drag-and-drop flow builder lets you create, launch, and optimize powerful agents 
            in minutes, not months. No developers needed.
          </p>
        </motion.div>

        {/* Agent Builder Preview */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="mb-16 relative mx-auto max-w-4xl"
        >
          <div className="rounded-xl overflow-hidden shadow-2xl border border-gray-200">
            <img 
              src={agentBuilderImage} 
              alt="WymBee Agent Flow Builder" 
              className="w-full h-auto"
              onError={(e) => {
                e.target.src = '/images/site/social_commerce_catalogs.png';
              }}
            />
          </div>
          <div className="absolute -bottom-5 left-1/2 transform -translate-x-1/2 bg-white px-6 py-3 rounded-full shadow-lg border border-gray-100">
            <p className="text-sm font-medium text-gray-800">Design complex agent behaviors with simple drag-and-drop</p>
          </div>
        </motion.div>

        {/* How It Works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-20"
        >
          <h3 className="text-2xl font-bold text-center mb-10">How It Works</h3>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-primary/5 rounded-xl p-6 h-full">
                  <div className="text-4xl font-bold text-primary/20 mb-4">{step.number}</div>
                  <h4 className="text-xl font-semibold mb-2">{step.title}</h4>
                  <p className="text-gray-600">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 z-10">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary/30" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Agent Types */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h3 className="text-2xl font-bold text-center mb-10">Ready-to-Launch WhatsApp Agents</h3>
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {agents.map((agent, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="relative group p-6 bg-white rounded-2xl border border-gray-100 hover:border-primary/20 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/5 group-hover:bg-primary/10 flex items-center justify-center text-primary">
                    {agent.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{agent.title}</h3>
                    <p className="text-gray-600">{agent.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Metrics Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16"
        >
          {metrics.map((metric, index) => (
            <div key={index} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary mb-1">{metric.value}</div>
              <div className="text-sm md:text-base text-gray-600">{metric.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Enhanced Testimonial */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-100 mb-16"
        >
          <blockquote className="text-center">
            <p className="text-lg text-gray-600 italic mb-4">
              "We replaced our entire customer service team's night shift with WymBee's WhatsApp agents. 
              Now our customers get instant responses at 2am, our team is happier, and we've seen sales 
              grow 3x from better service—all without spending a shilling on developers."
            </p>
            <footer className="text-gray-500">
              The Golf Hotel
            </footer>
          </blockquote>
        </motion.div>

        {/* Enhanced CTA */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mt-12 text-center"
        >
          <a 
            href="https://wymbee.com/account/signin/"
            className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-primary hover:bg-primary-hover rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
          >
            Put Your WhatsApp Agents to Work Today
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </a>
          <p className="mt-4 text-sm text-gray-500">No credit card required • Free 14-day trial</p>
        </motion.div>
      </Container>
    </motion.section>
  );
}